import { memo, useState, useRef, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  Chip
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import ArticleIcon from '@mui/icons-material/Article';
import ClearIcon from '@mui/icons-material/Clear';
import BaseModal from './common/BaseModal';
import { ServerURL } from './ServerURL';
import { getSentence } from './translations';

// Draggable Carousel Component
const DraggableCarousel = memo(({ contentType, selectedFormats, onFormatSelect, isDownloading }) => {
  const scrollRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  
  // Simple, efficient scroll state
  const scrollVelocity = useRef(0);
  const momentumAnimation = useRef(null);

  // Throttling refs for better performance
  const lastMouseMoveTime = useRef(0);
  const mouseMoveThreshold = 16; // ~60fps

  // Simple momentum animation for natural deceleration
  const startMomentum = useCallback((initialVelocity) => {
    if (!scrollRef.current || Math.abs(initialVelocity) < 0.5) return;
    
    // Never restart if momentum is already running
    if (momentumAnimation.current) {
      return;
    }
    
    scrollVelocity.current = initialVelocity;
    const friction = 0.96; // Slightly less friction for smoother momentum
    const minVelocity = 0.2; // Lower threshold for longer momentum
    
    const animate = () => {
      if (!scrollRef.current || Math.abs(scrollVelocity.current) < minVelocity) {
        momentumAnimation.current = null;
        scrollVelocity.current = 0;
        return;
      }
      
      // Apply friction to the stored velocity
      scrollVelocity.current *= friction;
      
      // Calculate new position
      const currentScroll = scrollRef.current.scrollLeft;
      const newScroll = currentScroll + scrollVelocity.current;
      
      // Apply bounds checking
      const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
      const clampedScroll = Math.max(0, Math.min(newScroll, maxScroll));
      
      // Stop at boundaries
      if (clampedScroll !== newScroll) {
        momentumAnimation.current = null;
        scrollVelocity.current = 0;
        return;
      }
      
      scrollRef.current.scrollLeft = clampedScroll;
      momentumAnimation.current = requestAnimationFrame(animate);
    };
    
    momentumAnimation.current = requestAnimationFrame(animate);
  }, []);

  // Stop momentum animation
  const stopMomentum = useCallback(() => {
    if (momentumAnimation.current) {
      cancelAnimationFrame(momentumAnimation.current);
      momentumAnimation.current = null;
    }
    scrollVelocity.current = 0;
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (!scrollRef.current) return;
    
    // Stop any momentum when user starts dragging
    stopMomentum();
    
    isDraggingRef.current = true;
    startXRef.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  }, [stopMomentum]);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || !scrollRef.current) return;
    
    // Throttle mouse move events for better performance
    const now = Date.now();
    if (now - lastMouseMoveTime.current < mouseMoveThreshold) return;
    lastMouseMoveTime.current = now;
    
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  }, []);

  const handleWheel = useCallback((e) => {
    if (!scrollRef.current) return;
    e.preventDefault();
    
    // Calculate scroll amount
    const scrollAmount = e.deltaY * 0.8;
    const currentPosition = scrollRef.current.scrollLeft;
    const targetPosition = currentPosition + scrollAmount;
    
    // Apply bounds checking
    const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
    const clampedPosition = Math.max(0, Math.min(targetPosition, maxScroll));
    
    // Apply scroll immediately - no interference with momentum
    scrollRef.current.scrollLeft = clampedPosition;
    
    // If momentum is running, just add to the velocity without any other changes
    if (momentumAnimation.current) {
      scrollVelocity.current += scrollAmount * 0.2; // Gentle addition to running momentum
      
      // Clamp velocity to prevent excessive speed
      const maxVelocity = 15;
      scrollVelocity.current = Math.max(-maxVelocity, Math.min(scrollVelocity.current, maxVelocity));
      return; // Don't do anything else - let momentum continue
    }
    
    // Only if momentum is NOT running, accumulate for potential momentum start
    scrollVelocity.current += scrollAmount * 0.4;
    
    // Clear any existing timeout
    clearTimeout(momentumAnimation.timeout);
    
    // Set timeout to start momentum only after wheel activity stops
    momentumAnimation.timeout = setTimeout(() => {
      // Only start momentum if it's not already running
      if (!momentumAnimation.current && Math.abs(scrollVelocity.current) > 2) {
        startMomentum(scrollVelocity.current);
      }
    }, 120); // Longer timeout to ensure wheel activity has stopped
  }, [startMomentum]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    // Add event listeners with proper options
    element.addEventListener('mousedown', handleMouseDown, { passive: false });
    element.addEventListener('mousemove', handleMouseMove, { passive: false });
    element.addEventListener('mouseup', handleMouseUp, { passive: true });
    element.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseup', handleMouseUp);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('wheel', handleWheel);
      
      // Clean up momentum animation
      if (momentumAnimation.current) {
        cancelAnimationFrame(momentumAnimation.current);
        momentumAnimation.current = null;
      }
      
      // Clean up any timeouts
      if (momentumAnimation.timeout) {
        clearTimeout(momentumAnimation.timeout);
        momentumAnimation.timeout = null;
      }
      
      scrollVelocity.current = 0;
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, handleWheel]);

  return (
    <Box 
      ref={scrollRef}
      sx={{ 
        display: 'flex', 
        gap: 1, 
        overflowX: 'auto',
        pb: 1,
        mx: 0,
        cursor: 'grab',
        userSelect: 'none',
        // Hide scrollbars completely
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Opera
        },
        // Smooth scrolling
        scrollBehavior: 'smooth',
      }}
    >
      {contentType.formats.map((format, index) => {
        const IconComponent = format.icon;
        const isSelected = selectedFormats[contentType.id] === format.value;
        const isFirst = index === 0;
        
        return (
          <Chip
            key={format.value}
            icon={<IconComponent />}
            label={format.label}
            variant={isSelected ? 'filled' : 'outlined'}
            clickable
            disabled={isDownloading}
            onClick={() => !isDownloading && onFormatSelect(contentType.id, format.value)}
            sx={{
              minWidth: 'max-content',
              flexShrink: 0,
              ml: isFirst ? 1.5 : 0,
              borderColor: isSelected ? format.color : 'rgba(0, 0, 0, 0.23)',
              color: isSelected ? 'white' : 'rgba(0, 0, 0, 0.87)',
              backgroundColor: isSelected ? format.color : 'transparent',
              '& .MuiChip-icon': {
                color: isSelected ? 'white' : 'rgba(0, 0, 0, 0.54)',
                transition: 'color 0.2s ease-in-out',
              },
              '&:hover': {
                backgroundColor: isSelected 
                  ? format.color 
                  : 'rgba(0, 0, 0, 0.04)',
              },
              transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
            }}
          />
        );
      })}
    </Box>
  );
});

DraggableCarousel.displayName = 'DraggableCarousel';

const DownloadModal = memo(({
  open,
  meetingId,
  onClose
}) => {
  const [selectedFormats, setSelectedFormats] = useState({
    summary: null,
    keypoints: null,
    transcript: null,
    audio: null
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const contentTypes = [
    {
      id: 'summary',
      title: getSentence(3).charAt(0).toUpperCase() + getSentence(3).slice(1),
      formats: [
        { value: 'pdf', label: 'PDF', icon: PictureAsPdfIcon, color: '#FF5722' },
        { value: 'docx', label: 'DOCX', icon: ArticleIcon, color: '#2E7D32' },
        { value: 'txt', label: 'TXT', icon: TextFieldsIcon, color: '#795548' },
        { value: 'odt', label: 'ODT', icon: DescriptionIcon, color: '#2196F3' },
        { value: 'md', label: 'MD', icon: CodeIcon, color: '#4CAF50' }
      ]
    },
    {
      id: 'keypoints',
      title: getSentence(4).charAt(0).toUpperCase() + getSentence(4).slice(1),
      formats: [
        { value: 'pdf', label: 'PDF', icon: PictureAsPdfIcon, color: '#FF5722' },
        { value: 'docx', label: 'DOCX', icon: ArticleIcon, color: '#2E7D32' },
        { value: 'txt', label: 'TXT', icon: TextFieldsIcon, color: '#795548' },
        { value: 'odt', label: 'ODT', icon: DescriptionIcon, color: '#2196F3' },
        { value: 'md', label: 'MD', icon: CodeIcon, color: '#4CAF50' }
      ]
    },
    {
      id: 'transcript',
      title: getSentence(5).charAt(0).toUpperCase() + getSentence(5).slice(1),
      formats: [
        { value: 'pdf', label: 'PDF', icon: PictureAsPdfIcon, color: '#FF5722' },
        { value: 'docx', label: 'DOCX', icon: ArticleIcon, color: '#2E7D32' },
        { value: 'txt', label: 'TXT', icon: TextFieldsIcon, color: '#795548' },
        { value: 'odt', label: 'ODT', icon: DescriptionIcon, color: '#2196F3' },
        { value: 'md', label: 'MD', icon: CodeIcon, color: '#4CAF50' }
      ]
    },
    {
      id: 'audio',
      title: getSentence(163),
      formats: [
        { value: 'mp3', label: 'MP3', icon: AudioFileIcon, color: '#9C27B0' }
      ]
    }
  ];

  const handleFormatSelect = (contentType, format) => {
    setSelectedFormats(prev => ({
      ...prev,
      [contentType]: format
    }));
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      // Download each selected format
      for (const [contentType, format] of Object.entries(selectedFormats)) {
        if (format) { // Only download if a format is selected (not null)
          await downloadContent(contentType, format);
        }
      }
      handleClose();
    } catch (error) {
      console.error('Error downloading content:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadContent = async (contentType, format) => {
    try {
      let url;
      let filename;
      
      switch (contentType) {
        case 'summary':
          url = `${ServerURL}/download_summary/${meetingId}/${format}`;
          filename = `summary_${meetingId}.${format}`;
          break;
        case 'keypoints':
          url = `${ServerURL}/download_keypoints/${meetingId}/${format}`;
          filename = `keypoints_${meetingId}.${format}`;
          break;
        case 'transcript':
          url = `${ServerURL}/download_transcript/${meetingId}/${format}`;
          filename = `transcript_${meetingId}.${format}`;
          break;
        case 'audio':
          url = `${ServerURL}/get_audio/${meetingId}/0/-1`;
          filename = `audio_${meetingId}.mp3`;
          break;
        default:
          return;
      }

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(`Error downloading ${contentType}:`, error);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset selections
    setSelectedFormats({
      summary: null,
      keypoints: null,
      transcript: null,
      audio: null
    });
    setIsDownloading(false);
  };

  const hasAnySelection = Object.values(selectedFormats).some(format => format !== null);

  const actions = (
    <>
      <Button 
        onClick={handleClose}
        variant="outlined"
        disabled={isDownloading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {getSentence(8)}
      </Button>
      <Button 
        onClick={handleDownloadAll}
        variant="contained"
        disabled={!hasAnySelection || isDownloading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {isDownloading ? getSentence(162) : getSentence(161)}
      </Button>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={getSentence(160)}
      size="medium"
      maxWidth="sm"
      actions={actions}
      contentSx={{ px: { xs: 1, sm: 1.5 }, pb: 2 }}
    >
      {/* Content Types with Format Carousels */}
      {contentTypes.map((contentType) => (
        <Box 
          key={contentType.id} 
          sx={{ 
            mb: 1.5,
            py: 1,
            px: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.2s ease-in-out',
            opacity: isDownloading ? 0.6 : 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, mx: 1.5 }}>
            <Typography 
              variant="subtitle1" 
              fontWeight={600}
              sx={{ 
                color: 'text.primary', 
                mr: 2,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {contentType.title}
            </Typography>
            
            {/* Clear Selection Chip */}
            <Chip
              icon={<ClearIcon />}
              label={getSentence(56).charAt(0).toUpperCase() + getSentence(56).slice(1)}
              variant={!selectedFormats[contentType.id] ? 'filled' : 'outlined'}
              clickable
              size="small"
              disabled={isDownloading}
              onClick={() => !isDownloading && handleFormatSelect(contentType.id, null)}
              sx={{
                minWidth: 'max-content',
                flexShrink: 0,
                ml: 1,
                borderColor: !selectedFormats[contentType.id] ? '#9E9E9E' : 'rgba(0, 0, 0, 0.23)',
                color: !selectedFormats[contentType.id] ? 'white' : 'rgba(0, 0, 0, 0.87)',
                backgroundColor: !selectedFormats[contentType.id] ? '#9E9E9E' : 'transparent',
                '& .MuiChip-icon': {
                  color: !selectedFormats[contentType.id] ? 'white' : 'rgba(0, 0, 0, 0.54)',
                  transition: 'color 0.2s ease-in-out',
                },
                '&:hover': {
                  backgroundColor: !selectedFormats[contentType.id] 
                    ? '#9E9E9E' 
                    : 'rgba(0, 0, 0, 0.04)',
                },
                transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
              }}
            />
          </Box>
          
          {/* Format Carousel */}
          <DraggableCarousel
            contentType={contentType}
            selectedFormats={selectedFormats}
            onFormatSelect={handleFormatSelect}
            isDownloading={isDownloading}
          />
        </Box>
      ))}
    </BaseModal>
  );
});

DownloadModal.displayName = 'DownloadModal';

export default DownloadModal;